import React, {useRef, useState} from "react";
import {Button, DialogActions} from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import {useMessages} from "i18n";
import BaseDialog from "components/BaseDialog";
import {focusOpener} from "components/sheetOpener";
import type {BackdropArt} from "./takeover/backdropArt";
import {SponsorTakeoverArt} from "./takeover/SponsorTakeover";
import {TEMPLATE_PREVIEW_ART_TESTID, TEMPLATE_PREVIEW_DIALOG_TESTID} from "./takeover/takeoverTestIds";
import {
	PreviewBody,
	PreviewCaption,
	ShowcaseStage,
	ViewLargerButton,
} from "./TemplatePreviewLauncher.styles";

/**
 * "See this one bigger" for a template picker tile (ui#588, owner: "the user should be able to see a
 * bigger preview of the effect").
 *
 * A tile is a MINIATURE — {@link TakeoverStage} — and a miniature is a legible icon of a treatment rather
 * than the treatment. It has to be: at 210px the ring's own cylinder is wider than the frame, so the tile
 * shows the composition scaled down and, for the ring, seen from slightly above so the circle reads. This
 * overlay is where that debt is paid back. It mounts the SAME renderer at showcase size with the
 * recipient's own level camera and full-size geometry, so nothing a sponsor buys is only ever seen as an
 * icon of itself.
 *
 * THE CONTROL IS NOT THE TILE. Every picker card is a `<label>` around a hidden radio, so a click anywhere
 * inside it selects that treatment — which is the picker's only control and must stay that way. The
 * owner's instruction was explicit ("do NOT overload tile click"), so this renders a button of its own,
 * OUTSIDE the label ({@link TemplateOption}), with an accessible name that says which treatment it opens.
 * Two controls, two meanings, no reading of the HTML spec required to know which one fired.
 *
 * WHAT "the real treatment with the real playback rules" MEANS HERE, precisely:
 *
 * - The same component, `SponsorTakeoverArt`, with the same effects, scrim and playback gating. A
 *   preview-only renderer would be a promise the product does not keep, which is the argument both
 *   pickers already make for mounting the real one.
 * - `surface="exhibit"`, so the art is HANDLEABLE exactly where the recipient's own showcase is: the
 *   styled layer restores pointer events only at or above the `showcase` breakpoint, so a wide window can
 *   drag the deck, spin the ring and scrub the band, and a narrow one gets the same passive composition a
 *   narrow recipient page gets. The rule is not re-decided here; it is inherited.
 * - `drive="sweep"`, and this is the one honest departure. The hero is SCROLL-linked on a recipient page,
 *   and a dialog has nothing to scroll — `scrollYProgress` would sit frozen and the overlay would show a
 *   still image of the one treatment sold on movement, which is the exact failure ui#540 exists to
 *   prevent. The sweep feeds the identical transforms from a self-playing clock: a different source, not a
 *   second animation.
 * - `stage` is left at its `full` default. That is what separates this from the tile, and why
 *   {@link TakeoverStage} is a prop rather than something derived from the drive — this is a `sweep` mount
 *   that must not be miniaturised.
 *
 * The layer's exhibit ANCHOR is skipped for a preview (see `TakeoverLayer`): the leftward shift exists
 * to pull the composition off a recipient's download card, and there is no card here to pull off.
 *
 * Dismissal and labelling are {@link BaseDialog}'s — MUI closes on Escape and on a backdrop click, and
 * labels the dialog from its own title.
 *
 * FOCUS RESTORATION IS NOT: this dialog restores focus itself, using the pattern
 * {@link ../legal/PolicyDialog} established — `disableRestoreFocus` tells MUI not to try, and
 * `onTransitionExited` puts focus back on the control once the overlay has finished leaving. The
 * explicit `disableRestoreFocus` keeps the local restore and MUI's own (working since ui#594) from
 * fighting over the same element.
 */
export interface TemplatePreviewLauncherProps {
	/** The art the tile is previewing — the sponsor's own uploads, or the placeholders. */
	art: BackdropArt | null;
	/** The treatment's name, for the control's accessible name and the dialog's title. */
	templateName: string;
	/** The treatment's own description, shown under the art so the overlay says what it is showing. */
	templateDescription: string;
}

export function TemplatePreviewLauncher(
	{art, templateName, templateDescription}: TemplatePreviewLauncherProps,
): React.ReactElement | null {
	const m = useMessages().templatePreview;
	const [open, setOpen] = useState(false);
	// The control to hand focus back to. A ref rather than "the element that had focus", because THIS is
	// the only element the overlay can correctly return a reader to — a mouse user may have had focus
	// nowhere at all when they clicked it.
	const trigger = useRef<HTMLButtonElement>(null);

	// No art is no preview to enlarge. Reachable only from the degenerate branch of `previewBackdropArt`,
	// but a control that opens an empty overlay is worse than no control.
	if (art === null || art.imageUrls.length === 0) {
		return null;
	}

	return (
		<>
			<ViewLargerButton
				ref={trigger}
				// The sponsor picker sits inside the booking `<form>` (`SponsorPage.tsx`), where a bare
				// <button> defaults to `submit` — so a click meant to look at a picture would try to buy
				// the takeover.
				type="button"
				aria-label={m.viewLargerAria(templateName)}
				title={m.viewLarger}
				aria-haspopup="dialog"
				onClick={() => setOpen(true)}
			>
				<OpenInFullIcon sx={{fontSize: 16}}/>
			</ViewLargerButton>

			{/*
			  * Driven by the `open` prop rather than wrapped in an "open && …" guard, which is how every
			  * other dialog in this app is written.
			  *
			  * The guard is tempting here because a picker mounts five of these and each one holds a
			  * treatment that animates. It buys nothing: `Modal` renders no children while shut (its
			  * `keepMounted` default is false), so a closed overlay is not a mounted treatment either way.
			  */}
			<BaseDialog
				title={m.title(templateName)}
				open={open}
				onClose={() => setOpen(false)}
				maxWidth="lg"
				// See the note above: this overlay returns focus itself rather than relying on the trap,
				// and `disableRestoreFocus` keeps the two from claiming the same element.
				// `onTransitionExited` rather than `onClose`, so focus moves once the overlay has actually
				// gone — moving it while the dialog is still fading out puts focus behind a modal.
				disableRestoreFocus
				onTransitionExited={() => focusOpener(trigger.current)}
				data-testid={TEMPLATE_PREVIEW_DIALOG_TESTID}
			>
				<PreviewBody dividers>
					<ShowcaseStage>
						<SponsorTakeoverArt
							art={art}
							testId={TEMPLATE_PREVIEW_ART_TESTID}
							drive="sweep"
							surface="exhibit"
						/>
					</ShowcaseStage>
					<PreviewCaption>{templateDescription}</PreviewCaption>
				</PreviewBody>
				<DialogActions>
					<Button onClick={() => setOpen(false)}>{m.close}</Button>
				</DialogActions>
			</BaseDialog>
		</>
	);
}
